import { motion } from "framer-motion";
import { Brain, ShieldCheck, TrendingUp, Cog, Plus, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const AI_IMG =
  "https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxjeWJlcnNlY3VyaXR5JTIwYWJzdHJhY3QlMjBkYXJrfGVufDB8fHx8MTc5MTU5MDY3M3ww&ixlib=rb-4.1.0&q=85";
const PMO_IMG =
  "https://images.unsplash.com/photo-1624555130581-1d9cca783bc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGNvbnN1bHRpbmclMjBtZWV0aW5nfGVufDB8fHx8MTc5MTU5MDY3M3ww&ixlib=rb-4.1.0&q=85";
const CYBER_IMG =
  "https://images.unsplash.com/photo-1510511459019-5dda7724fd87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxjeWJlcnNlY3VyaXR5JTIwYWJzdHJhY3QlMjBkYXJrfGVufDB8fHx8MTc5MTU5MDY3M3ww&ixlib=rb-4.1.0&q=85";
const RISK_IMG =
  "https://images.unsplash.com/photo-1511818966892-d7d671e672a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxnbGFzcyUyMHNreXNjcmFwZXIlMjBsb29raW5nJTIwdXAlMjBkYXJrJTIwc2t5fGVufDB8fHx8MTc5MTU5MTQ5OXww&ixlib=rb-4.1.0&q=85";

const PillarCard = ({ testid, tag, icon: Icon, title, desc, bullets, img, className = "", delay = 0, tall = false, wide = false }) => (
  <motion.article
    data-testid={testid}
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    className={`group relative flex flex-col overflow-hidden border border-white/10 bg-surface transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-gold/40 ${wide ? "md:col-span-12 md:flex-row" : ""} ${className}`}
  >
    {img && (
      <div className={`relative overflow-hidden ${wide ? "h-48 md:h-auto md:w-[42%]" : tall ? "h-56 lg:h-72" : "h-40"}`}>
        <img src={img} alt="" aria-hidden="true" className="img-treated h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/30 to-transparent md:bg-gradient-to-r" />
      </div>
    )}
    <div className={`flex flex-1 flex-col p-8 lg:p-10 ${wide ? "md:w-[58%]" : ""}`}>
      <div className="flex items-center justify-between">
        <Icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">{tag}</span>
      </div>
      <h3 className="mt-6 font-display text-2xl tracking-wide lg:text-3xl">{title}</h3>
      <p className="mt-4 text-sm font-light leading-relaxed text-slate-400 sm:text-base">{desc}</p>
      <ul className="mt-6 flex-1 space-y-3">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-sm font-light text-slate-300">
            <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
            {b}
          </li>
        ))}
      </ul>
      <button
        data-testid={`${testid}-cta`}
        onClick={() => scrollToId("contact")}
        className="mt-8 flex items-center gap-2 self-start font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:text-gold"
      >
        Discuss this
        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </button>
    </div>
  </motion.article>
);

export default function Services() {
  return (
    <section id="services" className="relative py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <Reveal>
              <Eyebrow>Capabilities</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-display text-4xl tracking-wide sm:text-5xl">
                Four disciplines.
                <br />
                <span className="gold-text">One line of sight.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="max-w-sm">
            <p className="text-sm font-light leading-relaxed text-slate-400 sm:text-base">
              Governed AI, hardened security, managed risk and disciplined delivery are
              engineered together — never bought separately.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-5 md:auto-rows-fr md:grid-cols-12 lg:gap-6">
          <PillarCard
            testid="service-card-ai-governance"
            tag="01"
            icon={Brain}
            title="AI Governance"
            desc="Deploy AI at enterprise speed with the frameworks, controls and evidence regulators expect — led by AIGP-certified practitioners."
            bullets={[
              "EU AI Act & regulatory readiness",
              "AI policy, ethics & model-risk frameworks",
              "GRC integration & audit evidence",
              "AIGP-certified advisory & enablement",
            ]}
            img={AI_IMG}
            tall
            className="md:col-span-8 md:row-span-2"
          />
          <PillarCard
            testid="service-card-cybersecurity"
            tag="02"
            icon={ShieldCheck}
            title="Cybersecurity"
            desc="Protect what matters — resilience your board can verify."
            bullets={[
              "Zero-trust architecture",
              "NIST & ISO 27001 alignment",
              "Incident readiness & board reporting",
            ]}
            img={CYBER_IMG}
            className="md:col-span-4"
            delay={0.1}
          />
          <PillarCard
            testid="service-card-enterprise-risk"
            tag="03"
            icon={TrendingUp}
            title="Enterprise Risk"
            desc="Turn risk into opportunity with decisions your board can stand behind."
            bullets={[
              "Enterprise risk identification & quantification",
              "Risk-adjusted decision frameworks",
              "Regulatory & third-party exposure mapping",
            ]}
            img={RISK_IMG}
            className="md:col-span-4"
            delay={0.2}
          />
          <PillarCard
            testid="service-card-pmo"
            tag="04"
            icon={Cog}
            title="PMO & Transformation"
            desc="Deliver strategy with precision — turn your PMO from reporting overhead into the engine of delivery."
            bullets={[
              "EPMO design & stand-up",
              "Portfolio, programme & delivery governance",
              "Tooling, reporting & delivery velocity",
            ]}
            img={PMO_IMG}
            wide
            delay={0.15}
          />
        </div>
      </div>
    </section>
  );
}
